import { Message, useQueryGetMessageUsage } from "@apis:message/message";
import { useCheckMessageTokenUsage } from "@hooks:useCheckMessageTokenUsage";
import { Box, Tooltip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LuInfo } from "react-icons/lu";

interface MessageUsageProps {
  messageId?: Message["GET_ONE"]["Response"]["id"];
}

const formatTokens = (count: number) => count.toLocaleString();

/**
 * An info icon showing the message's token usage on hover.
 * Only rendered when the "Show Message Token Usage" UI preference is enabled.
 * The usage is queried when the tooltip is opened.
 */
export const MessageUsage = ({ messageId }: MessageUsageProps) => {
  const { t } = useTranslation();
  const { isTokenUsageVisible } = useCheckMessageTokenUsage();
  const [opened, setOpened] = useState(false);

  // Drafts carry the placeholder ID "new" until the answer is saved
  const savedMessageId = messageId && messageId !== "new" ? messageId : "";

  const {
    data: usage,
    isFetching,
    isError,
    refetch,
  } = useQueryGetMessageUsage(
    { message_id: savedMessageId },
    {
      enabled: opened && isTokenUsageVisible && !!savedMessageId,
      staleTime: Infinity,
      throwOnError: false,
    },
  );

  if (!isTokenUsageVisible || !savedMessageId) return null;

  const onOpen = () => {
    // A hover right after a streamed answer can race the stream's end and cache null;
    // retry failures and nulls on the next open instead of caching them
    if ((usage === null || isError) && !isFetching) {
      refetch();
    }
    setOpened(true);
  };

  const onClose = () => {
    setOpened(false);
  };

  const details = usage?.usageDetails ?? [];
  const isEstimated = details.some((detail) => detail.estimated);

  const usageContent = () => {
    if (isError) {
      return (
        <Typography variant="body2">{t("chat.usage.couldNotLoad")}</Typography>
      );
    }

    if (isFetching || usage === undefined) {
      return <Typography variant="body2">{t("common.loading")}</Typography>;
    }

    if (usage === null) {
      return <Typography variant="body2">{t("chat.usage.none")}</Typography>;
    }

    return (
      <>
        <Typography variant="body2">
          {t("chat.usage.inputTokens", {
            value: formatTokens(usage.inputTokens ?? 0),
          })}
        </Typography>
        <Typography variant="body2">
          {t("chat.usage.outputTokens", {
            value: formatTokens(usage.outputTokens ?? 0),
          })}
        </Typography>
        {details.length > 1 &&
          details.map((detail) => (
            <Typography
              key={`${detail.provider}/${detail.model}`}
              variant="caption"
              component="div"
              sx={{ marginTop: "0.25rem", marginLeft: "0.5rem" }}
            >
              {t("chat.usage.detail", {
                model: detail.model,
                provider: detail.provider,
                input: formatTokens(detail.inputTokens ?? 0),
                output: formatTokens(detail.outputTokens ?? 0),
              })}
            </Typography>
          ))}
        {isEstimated && (
          <Typography
            variant="caption"
            component="div"
            sx={{ fontStyle: "italic" }}
          >
            {t("chat.usage.estimated")}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Tooltip
      onOpen={onOpen}
      onClose={onClose}
      title={<Box sx={{ padding: "0.25rem" }}>{usageContent()}</Box>}
    >
      <IconButton size="small" aria-label={t("chat.usage.ariaLabel")}>
        <LuInfo />
      </IconButton>
    </Tooltip>
  );
};
